import axios, { AxiosProgressEvent } from "axios";
import api from "./capTableApi";
import {
  CompanyDocumentBrowserResponse,
  CompanyDocumentSearchRequest,
  CompanyDocumentSearchResponse,
  CompanyDocumentDownloadUrlRequest,
  CompanyDocumentDownloadUrlResponse,
  CompleteCompanyDocumentUploadsRequest,
  CompleteCompanyDocumentUploadsResponse,
  CreateCompanyDocumentFolderRequest,
  DeleteCompanyDocumentItemsRequest,
  DeleteCompanyDocumentItemsResponse,
  MoveCompanyDocumentFilesRequest,
  MoveCompanyDocumentFilesResponse,
  CompanyDocumentFolder,
  PresignCompanyDocumentUploadsRequest,
  PresignCompanyDocumentUploadsResponse,
  QueueCompanyDocumentDownloadRequest,
  QueueCompanyDocumentDownloadResponse,
  RelatedCompanyDocument,
  RelatedDocumentFilter,
  VaultDownloadNotification,
  normalizeVaultDownloadNotification,
} from "../types/companyDocuments";

type ResponseEnvelope<T> = {
  data?: T;
};

function unwrapResponse<T>(payload: T | ResponseEnvelope<T>): T {
  if (
    typeof payload === "object" &&
    payload !== null &&
    Object.prototype.hasOwnProperty.call(payload, "data")
  ) {
    return (payload as ResponseEnvelope<T>).data as T;
  }
  return payload as T;
}

function asErrorMessage(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value !== "object" || value === null) return undefined;
  const error = value as Record<string, unknown>;
  return (
    (typeof error.errorMessage === "string" && error.errorMessage) ||
    (typeof error.message === "string" && error.message) ||
    undefined
  );
}

export function getCompanyDocumentError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const message = asErrorMessage(error.response?.data);
    if (message) return message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

export async function getCompanyDocumentBrowser(
  folderId: string | null
): Promise<CompanyDocumentBrowserResponse> {
  const response = await api.get("/v1/company/documentVault/browser", {
    params: folderId ? { folderId } : undefined,
  });
  return unwrapResponse<CompanyDocumentBrowserResponse>(response.data);
}

export async function searchCompanyDocuments(
  request: CompanyDocumentSearchRequest
): Promise<CompanyDocumentSearchResponse> {
  const params = Object.fromEntries(
    Object.entries(request).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );
  const response = await api.get("/v1/company/documentVault/search", {
    params,
  });
  return unwrapResponse<CompanyDocumentSearchResponse>(response.data);
}

export async function createCompanyDocumentFolder(
  request: CreateCompanyDocumentFolderRequest
): Promise<CompanyDocumentFolder> {
  const response = await api.post("/v1/company/documentVault/folders", request);
  return unwrapResponse<CompanyDocumentFolder>(response.data);
}

export async function presignCompanyDocumentUploads(
  request: PresignCompanyDocumentUploadsRequest
): Promise<PresignCompanyDocumentUploadsResponse> {
  const response = await api.post(
    "/v1/company/documentVault/uploads/presign",
    request
  );
  return unwrapResponse<PresignCompanyDocumentUploadsResponse>(response.data);
}

export async function completeCompanyDocumentUploads(
  request: CompleteCompanyDocumentUploadsRequest
): Promise<CompleteCompanyDocumentUploadsResponse> {
  const response = await api.post(
    "/v1/company/documentVault/uploads/complete",
    request
  );
  return unwrapResponse<CompleteCompanyDocumentUploadsResponse>(response.data);
}

export async function putCompanyDocumentToS3({
  uploadUrl,
  file,
  headers,
  signal,
  onProgress,
}: {
  uploadUrl: string;
  file: File;
  headers: Record<string, string>;
  signal: AbortSignal;
  onProgress: (percentage: number) => void;
}): Promise<void> {
  await axios.put(uploadUrl, file, {
    // S3 receives exactly the headers returned by the presign endpoint. The
    // CapTable API Axios instance is deliberately not used here.
    headers,
    signal,
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      if (!progressEvent.total) return;
      onProgress(
        Math.round((progressEvent.loaded / progressEvent.total) * 100)
      );
    },
  });
}

export async function getRelatedCompanyDocuments(
  source: RelatedDocumentFilter
): Promise<RelatedCompanyDocument[]> {
  const response = await api.get("/v1/company/documentVault/related", {
    params: source === "ALL" ? undefined : { source },
  });
  const payload = unwrapResponse<
    RelatedCompanyDocument[] | { documents?: RelatedCompanyDocument[] }
  >(response.data);
  return Array.isArray(payload) ? payload : payload.documents || [];
}

export async function getCompanyDocumentDownloadUrl(
  request: CompanyDocumentDownloadUrlRequest
): Promise<CompanyDocumentDownloadUrlResponse> {
  const response = await api.post(
    "/v1/company/documentVault/download-url",
    request
  );
  return unwrapResponse<CompanyDocumentDownloadUrlResponse>(response.data);
}

export async function queueCompanyDocumentDownload(
  request: QueueCompanyDocumentDownloadRequest
): Promise<QueueCompanyDocumentDownloadResponse> {
  const response = await api.post(
    "/v1/company/documentVault/download-bundle",
    request
  );
  return unwrapResponse<QueueCompanyDocumentDownloadResponse>(response.data);
}

export async function deleteCompanyDocumentItems(
  request: DeleteCompanyDocumentItemsRequest
): Promise<DeleteCompanyDocumentItemsResponse> {
  const response = await api.delete("/v1/company/documentVault/items", {
    data: request,
  });
  return unwrapResponse<DeleteCompanyDocumentItemsResponse>(response.data);
}

export async function moveCompanyDocumentFiles(
  request: MoveCompanyDocumentFilesRequest
): Promise<MoveCompanyDocumentFilesResponse> {
  const response = await api.patch(
    "/v1/company/documentVault/files/move",
    request
  );
  return unwrapResponse<MoveCompanyDocumentFilesResponse>(response.data);
}

export async function getCompanyDocumentDownloadNotifications(): Promise<
  VaultDownloadNotification[]
> {
  const response = await api.get(
    "/v1/company/documentVault/download-notifications",
    { params: { limit: 50 } }
  );
  const payload = unwrapResponse<
    unknown[] | { notifications?: unknown[]; items?: unknown[] }
  >(response.data);
  const notifications = Array.isArray(payload)
    ? payload
    : payload.notifications || payload.items || [];
  return notifications
    .map(normalizeVaultDownloadNotification)
    .filter((notification): notification is VaultDownloadNotification =>
      Boolean(notification)
    );
}

export async function markCompanyDocumentDownloadNotificationRead(
  notificationId: string
): Promise<void> {
  await api.patch(
    `/v1/company/documentVault/download-notifications/${notificationId}/read`
  );
}

export async function getFreshCompanyDocumentDownloadNotificationUrl(
  notificationId: string
): Promise<VaultDownloadNotification> {
  const response = await api.post(
    `/v1/company/documentVault/download-notifications/${notificationId}/download-url`
  );
  const notification = normalizeVaultDownloadNotification(
    unwrapResponse<unknown>(response.data)
  );
  if (!notification) {
    throw new Error("The download link was not returned.");
  }
  return notification;
}

export function getCompanyDocumentDownloadEventsUrl({
  accessToken,
  companyId,
}: {
  accessToken: string;
  companyId: string;
}): string {
  const baseUrl = (
    api.defaults.baseURL ||
    process.env.REACT_APP_NEW_CAPTABLE_API ||
    ""
  ).replace(/\/$/, "");
  const query = new URLSearchParams({
    AccessToken: accessToken,
    companyId,
  });
  return `${baseUrl}/v1/company/documentVault/download-events?${query.toString()}`;
}
